import React, { useCallback, useEffect } from 'react';
import { authGoogleUserWithAPI } from '../../services/authentication/authWithGoogle';
import { setUserSession } from '../../store/slices/UserSession/userSession';
import { setTemporalSession } from '../../utils/tempSession';
import { usePageLoader } from '../../context/pageLoader';
import { Redirect, useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import useIonicToast from '../../hooks/useIonicToast';
import { IonContent, IonPage } from '@ionic/react';

export default function GoogleAuth() {
  const { setBusy } = usePageLoader();
  const history = useHistory();
  const dispatch = useDispatch();
  const { presentIonToast } = useIonicToast();

  const params = new URLSearchParams(location.hash.substring(1));
  const id_token = params?.get('id_token');
  const googleError = params?.get('error');
  const isReturningFromGoogle = Boolean(id_token || googleError);

  // GOOGLE AUTHENTICATION
  const authenticateGoogleUser = useCallback(
    async (id_token: string) => {
      try {
        setBusy(true);
        const res = await authGoogleUserWithAPI(id_token);

        //REDIRECT USER WHEN THEY HAVE AN ACCOUNT ON SWYFT BUT DO NOT HAVE "STORE_OWNER" ROLE
        if ((res.code as unknown as number) === 220) {
          setTemporalSession({ ...res.user, isGoogleUser: true });
          return history.replace('/auth/sign-in');
        }

        // A genuine sign-in MUST carry an access token. Without one, dispatching
        // a session leaves the app "logged in" with a null token and every
        // request fails. Surface the message and return to sign-in.
        if (!res?.signedAccessToken) {
          presentIonToast(
            (res?.details as string) ||
              (res?.message as string) ||
              'Unable to sign in with Google. Please try again.',
            'error'
          );
          return history.replace('/auth/sign-in');
        }

        //STORE USER SESSION AND CLEAN UP URL
        dispatch(setUserSession(res));
        return history.replace('/dashboard');
      } catch (error: any) {
        console.error(error);
        presentIonToast(error.message, 'error');
        return history.replace('/auth/sign-in');
      } finally {
        setBusy(false);
      }
    },
    [setBusy, dispatch, history, presentIonToast]
  );

  useEffect(() => {
    if (id_token) {
      authenticateGoogleUser(id_token);
    } else if (googleError) {
      presentIonToast('Google authentication failed, try again', 'error');
      return history.replace('/auth/sign-in');
    }
  }, []);

  if (!isReturningFromGoogle) return <Redirect to="/dashboard" />;

  return (
    <IonPage>
      <IonContent>
        <div className="w-full h-full flex items-center justify-center">
          <h5 className="medium-emphasis-text animate-pulse">
            verifying with google...
          </h5>
        </div>
      </IonContent>
    </IonPage>
  );
}
